// ---------------------------------------------------------------------------
// DnD Email Editor – Plain Text Compiler
// ---------------------------------------------------------------------------

import type { DndEmailModel, Layer, Element, ColumnsLayer } from './dnd-model';
import { htmlToPlainText } from '@/components/ftp-editor/ftp-editor-utils';

// Reuse the HTML editor's converter so plain-text generation matches it exactly —
// notably rendering links as "text (url)" instead of dropping the anchor URL.
function stripHtml(html: string): string {
  return htmlToPlainText(html);
}

function elementToPlainText(el: Element): string {
  switch (el.type) {
    case 'text':
      return stripHtml(el.content);
    case 'image':
      return el.alt ? `[Image: ${el.alt}]` : '';
    case 'button':
      return el.href ? `${el.text}: ${el.href}` : el.text;
    case 'divider':
      return '---';
    case 'spacer':
      return '';
    case 'html':
      return stripHtml(el.content);
    case 'social':
      return el.links
        .filter((l) => l.enabled && l.url)
        .map((l) => `${l.platform.charAt(0).toUpperCase() + l.platform.slice(1)}: ${l.url}`)
        .join('\n');
  }
}

function layerToPlainText(layer: Layer): string {
  switch (layer.type) {
    case 'text':
      return stripHtml(layer.content);

    case 'image':
      return layer.alt ? `[Image: ${layer.alt}]` : '';

    case 'button':
      return layer.href ? `${layer.text}: ${layer.href}` : layer.text;

    case 'divider':
      return '---';

    case 'spacer':
      return '';

    case 'html':
      return stripHtml(layer.content);

    case 'columns': {
      const cols = (layer as ColumnsLayer).columns;
      return cols
        .map((col) =>
          col.elements
            .map(elementToPlainText)
            .filter(Boolean)
            .join('\n'),
        )
        .filter(Boolean)
        .join('\n\n');
    }

    case 'social':
      return layer.links
        .filter((l) => l.enabled && l.url)
        .map((l) => `${l.platform.charAt(0).toUpperCase() + l.platform.slice(1)}: ${l.url}`)
        .join('\n');

    case 'video':
      return layer.videoUrl ? `Watch video: ${layer.videoUrl}` : '';

    case 'countdown':
      return `Countdown to: ${layer.targetDate}`;
  }
}

export function compileToPlainText(model: DndEmailModel): string {
  const parts: string[] = [];

  if (model.preheaderText) {
    parts.push(model.preheaderText);
    parts.push('');
  }

  for (const layer of model.layers) {
    const text = layerToPlainText(layer);
    if (text) parts.push(text);
  }

  return parts.join('\n\n').replace(/\n{3,}/g, '\n\n').trim();
}
